import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  Grid,
  Hidden,
  Paper,
  Snackbar,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import TuneIcon from '@material-ui/icons/Tune';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import { Link, useLocation } from 'react-router-dom';
import SectionHeader from '../components/common/SectionHeader';
import EmptyState from '../components/common/EmptyState';
import FilterBar from '../components/explore/FilterBar';
import MapPanel from '../components/explore/MapPanel';
import ComparePanel from '../components/explore/ComparePanel';
import PlaceCardV2 from '../components/common/PlaceCardV2';
import { resolveSearchLocation, searchPlaces } from '../services/placesService';
import { getForecast } from '../services/weatherService';
import { useTravelGuide } from '../context/TravelGuideContext';

const DEFAULT_COORDS = { lat: 28.6139, lng: 77.209 };
const DEFAULT_DELTA = 0.08;

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  masthead: {
    borderRadius: 26,
    border: '1px solid rgba(166, 140, 118, 0.28)',
    background: 'linear-gradient(120deg, #fff9f2 0%, #f7ecde 58%, #efe0d1 130%)',
    boxShadow: '0 14px 28px rgba(63, 46, 31, 0.1)',
    padding: theme.spacing(3, 3, 2.2, 3),
    marginBottom: theme.spacing(2),
  },
  mastTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  mastTitle: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: '#2f261f',
  },
  mastSub: {
    color: '#645649',
    maxWidth: 620,
    marginTop: theme.spacing(0.5),
  },
  quickPills: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  chip: {
    background: 'rgba(255, 248, 239, 0.95)',
    border: '1px solid rgba(166, 140, 118, 0.35)',
    color: '#4d4033',
    fontWeight: 700,
  },
  sticky: {
    position: 'sticky',
    top: 106,
  },
  listWrap: {
    maxHeight: '80vh',
    overflow: 'auto',
    paddingRight: theme.spacing(1.2),
    paddingLeft: theme.spacing(0.5),
    paddingBottom: theme.spacing(1.5),
    scrollbarGutter: 'stable',
  },
  compareButton: {
    borderColor: 'rgba(112, 92, 76, 0.36)',
    color: '#4a3d32',
    background: 'linear-gradient(120deg, #fff8ef 0%, #f5eadc 100%)',
    borderRadius: 12,
    '&:hover': {
      borderColor: '#8f6a52',
      backgroundColor: 'rgba(255, 248, 239, 0.95)',
    },
  },
  panel: {
    border: '1px solid rgba(169, 144, 124, 0.28)',
    borderRadius: 18,
    background: 'linear-gradient(180deg, #fffdf9 0%, #fff7ef 100%)',
    padding: theme.spacing(1.2),
  },
  listPanel: {
    border: '1px solid rgba(169, 144, 124, 0.28)',
    borderRadius: 18,
    background: 'linear-gradient(180deg, #fffdf9 0%, #fff8f0 100%)',
    padding: theme.spacing(1.2, 0.4, 1.2, 0.4),
  },
  drawerBody: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTop: '1px solid rgba(169, 144, 124, 0.3)',
    background: 'linear-gradient(180deg, #fffdf9 0%, #f8efe3 100%)',
  },
  permissionBar: {
    marginTop: theme.spacing(1.2),
    borderRadius: 12,
    border: '1px solid rgba(169, 144, 124, 0.35)',
    background: '#fff8ef',
    padding: theme.spacing(1, 1.2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
}));

const hasCoordChanged = (current, next, epsilon = 0.0001) => (
  Math.abs((current?.lat || 0) - (next?.lat || 0)) > epsilon
  || Math.abs((current?.lng || 0) - (next?.lng || 0)) > epsilon
);

const hasBoundsChanged = (current, next, epsilon = 0.0001) => {
  if (!current) return true;
  return (
    Math.abs((current?.ne?.lat || 0) - (next?.ne?.lat || 0)) > epsilon
    || Math.abs((current?.ne?.lng || 0) - (next?.ne?.lng || 0)) > epsilon
    || Math.abs((current?.sw?.lat || 0) - (next?.sw?.lat || 0)) > epsilon
    || Math.abs((current?.sw?.lng || 0) - (next?.sw?.lng || 0)) > epsilon
  );
};

const buildBoundsFromCenter = (center, delta = DEFAULT_DELTA) => ({
  ne: { lat: center.lat + delta, lng: center.lng + delta },
  sw: { lat: center.lat - delta, lng: center.lng - delta },
});

const ExplorePage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { state, dispatch } = useTravelGuide();
  const [filters, setFilters] = useState({
    type: 'restaurants',
    rating: '0',
    priceLevel: 'any',
    openNow: false,
    distanceKm: '0',
    sortBy: 'relevance',
    query: new URLSearchParams(location.search).get('q') || '',
  });
  const [coords, setCoords] = useState(DEFAULT_COORDS);
  const [bounds, setBounds] = useState(null);
  const [weatherData, setWeatherData] = useState({ list: [] });
  const [places, setPlaces] = useState([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [permissionMessage, setPermissionMessage] = useState('');
  const [permissionToastOpen, setPermissionToastOpen] = useState(false);
  const resetFilters = () => {
    setFilters((prev) => ({
      ...prev,
      rating: '0',
      priceLevel: 'any',
      openNow: false,
      distanceKm: '0',
      sortBy: 'relevance',
    }));
  };

  useEffect(() => {
    const nextQuery = new URLSearchParams(location.search).get('q') || '';
    setFilters((prev) => (prev.query === nextQuery ? prev : { ...prev, query: nextQuery }));
  }, [location.search]);

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q') || '';
    if (!query.trim()) return;
    let active = true;

    resolveSearchLocation(query).then((resolved) => {
      if (!active || !resolved) return;
      setCoords((prev) => (hasCoordChanged(prev, resolved) ? resolved : prev));
      setBounds(buildBoundsFromCenter(resolved));
      setSelectedPlaceId('');
    });

    return () => {
      active = false;
    };
  }, [location.search]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords: position }) => {
        setCoords({ lat: position.latitude, lng: position.longitude });
        setLocationPermission('granted');
        setPermissionMessage('');
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermission('denied');
          setPermissionMessage('Location access is denied. Showing default/map searched area. You can enable location and retry.');
          setPermissionToastOpen(true);
          return;
        }
        setLocationPermission('unavailable');
        setPermissionMessage('Could not access your location right now. Showing available area data.');
        setPermissionToastOpen(true);
      },
      { timeout: 8000, maximumAge: 30000 },
    );
  }, []);

  const retryLocationPermission = () => {
    if (!navigator.geolocation) {
      setLocationPermission('unsupported');
      setPermissionMessage('Geolocation is not supported in this browser.');
      setPermissionToastOpen(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords: position }) => {
        setCoords({ lat: position.latitude, lng: position.longitude });
        setBounds(buildBoundsFromCenter({ lat: position.latitude, lng: position.longitude }));
        setLocationPermission('granted');
        setPermissionMessage('');
        setPermissionToastOpen(false);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermission('denied');
          setPermissionMessage('Location permission is still denied. Please allow it in browser site settings.');
        } else {
          setLocationPermission('unavailable');
          setPermissionMessage('Location is unavailable at the moment. Please try again.');
        }
        setPermissionToastOpen(true);
      },
      { timeout: 8000, maximumAge: 30000 },
    );
  };

  useEffect(() => {
    if (!bounds) return;
    let active = true;
    setLoading(true);
    setError('');
    searchPlaces({
      type: filters.type,
      bounds,
      filters,
      center: coords,
    })
      .then((result) => {
        if (!active) return;
        setPlaces(result);
        dispatch({ type: 'UPSERT_CATALOG', payload: result });
      })
      .catch(() => {
        if (!active) return;
        setError('Unable to fetch places right now. Try changing area or filters.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [bounds, filters, coords, dispatch]);

  useEffect(() => {
    let active = true;
    getForecast(coords).then((data) => {
      if (!active) return;
      setWeatherData(data || { list: [] });
    });
    return () => {
      active = false;
    };
  }, [coords]);

  const selectedPlace = useMemo(
    () => places.find((place) => place.id === selectedPlaceId),
    [places, selectedPlaceId],
  );

  const filterChange = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));

  return (
    <>
      <Container maxWidth="xl" className={classes.root}>
        <Paper elevation={0} className={classes.masthead}>
          <Box className={classes.mastTop}>
            <Box>
              <Typography variant="h4" className={classes.mastTitle}>Explore smarter with map context</Typography>
              <Typography variant="body1" className={classes.mastSub}>
                Filter by rating, distance, and price to find the right place faster.
              </Typography>
            </Box>
            <Box display="flex" gridGap={8}>
              <Button component={Link} to="/itinerary" variant="contained" color="primary">Open Itinerary</Button>
              <Button component={Link} to="/saved" variant="outlined" color="primary">Saved Places</Button>
            </Box>
          </Box>
          <Box className={classes.quickPills}>
            <Chip className={classes.chip} label={`${places.length} places`} />
            <Chip className={classes.chip} label={`Type: ${filters.type}`} />
            <Chip className={classes.chip} label={`Sort: ${filters.sortBy}`} />
            <Chip className={classes.chip} label={filters.query ? `Search: ${filters.query}` : 'No search query'} />
          </Box>
          {locationPermission !== 'granted' && permissionMessage && (
            <div className={classes.permissionBar}>
              <Typography variant="body2" color="textSecondary">{permissionMessage}</Typography>
              <Button size="small" color="primary" variant="outlined" onClick={retryLocationPermission}>
                Retry Location
              </Button>
            </div>
          )}
        </Paper>

        <SectionHeader
          title="Search Results"
          subtitle="Refine filters and select markers to inspect places quickly."
          rightSlot={(
            <Box display="flex" gridGap={8}>
              <Button
                startIcon={<CompareArrowsIcon />}
                variant="outlined"
                className={classes.compareButton}
                onClick={() => setCompareOpen(true)}
              >
                Compare ({state.comparePlaces.length})
              </Button>
              {isMobile && (
                <Button
                  startIcon={<TuneIcon />}
                  variant="contained"
                  color="primary"
                  style={{ borderRadius: 12 }}
                  onClick={() => setMobileFilterOpen(true)}
                >
                  Filters
                </Button>
              )}
            </Box>
          )}
        />

        <Grid container spacing={2}>
          <Hidden smDown>
            <Grid item md={3}>
              <div className={classes.sticky}>
                <div className={classes.panel}>
                  <FilterBar filters={filters} onChange={filterChange} onReset={resetFilters} />
                </div>
                {selectedPlace && (
                  <Box mt={2} className={classes.panel}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Selected</Typography>
                    <PlaceCardV2 place={selectedPlace} compact />
                  </Box>
                )}
              </div>
            </Grid>
          </Hidden>

          <Grid item xs={12} md={6}>
            <div className={classes.panel}>
              <MapPanel
                coords={coords}
                places={places}
                selectedId={selectedPlaceId}
                weatherData={weatherData}
                onSelect={setSelectedPlaceId}
                onBoundsChange={({ center, bounds: nextBounds }) => {
                  setCoords((prev) => (hasCoordChanged(prev, center) ? center : prev));
                  setBounds((prev) => (hasBoundsChanged(prev, nextBounds) ? nextBounds : prev));
                }}
              />
            </div>
          </Grid>

          <Grid item xs={12} md={3}>
            <div className={classes.listPanel}>
              <Typography variant="subtitle1" style={{ fontWeight: 800, margin: '0.3rem 0.8rem 0.8rem' }}>
                Nearby picks
              </Typography>
              <div className={classes.listWrap}>
                {loading && (
                  <Box py={6} textAlign="center"><CircularProgress color="primary" /></Box>
                )}
                {!loading && error && (
                  <EmptyState title="Could not load places" description={error} />
                )}
                {!loading && !error && places.length === 0 && (
                  <EmptyState
                    title="No places found"
                    description="Try relaxing filters, zooming out, or searching another area."
                  />
                )}
                {!loading && places.map((place) => (
                  <Box key={place.id} mb={2}>
                    <PlaceCardV2 place={place} compact />
                  </Box>
                ))}
              </div>
            </div>
          </Grid>
        </Grid>
      </Container>

      <Drawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
      >
        <Box p={2} className={classes.drawerBody}>
          <FilterBar filters={filters} onChange={filterChange} onReset={resetFilters} />
          <Box textAlign="right">
            <Button color="primary" variant="contained" onClick={() => setMobileFilterOpen(false)}>
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>

      <ComparePanel open={compareOpen} onClose={() => setCompareOpen(false)} />
      <Snackbar
        open={permissionToastOpen}
        autoHideDuration={4200}
        onClose={() => setPermissionToastOpen(false)}
        message={permissionMessage}
      />
    </>
  );
};

export default ExplorePage;
